import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import { Dialog } from "primereact/dialog";
import TabHeader from "../../common/TabHeader";
import { Button } from "primereact/button";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../../constants";
import { getReadableDate } from "../../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../../style";

export default function DialogAddUserToBatch({ visible, userId, setBatches, closeDialog }) {
    const { requestAPI, showToast } = useAppContext();

    const [loading, setLoading] = useState();
    const [assigning, setAssigning] = useState();
    const [availableBatches, setAvailableBatches] = useState();
    const [error, setError] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: `users/${userId}/assignable-batches`,
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setAvailableBatches(data);
                } else {
                    setError("Couldn't load Batches");
                }
            },
        });
    }, [requestAPI, userId]);

    const assignToBatch = useCallback(
        (batchId) => {
            requestAPI({
                requestPath: `batches/${batchId}/users`,
                requestMethod: "POST",
                requestPostBody: { user_id: Number(userId) },
                setLoading: setAssigning,
                onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add To Batch !", life: 2000 }),
                onResponseReceieved: (assigned, responseCode) => {
                    if (assigned && responseCode === 201) {
                        showToast({ severity: "success", summary: "Assigned", detail: "Student Added To Batch", life: 1000 });
                        requestAPI({
                            requestPath: `users/${userId}/batches`,
                            requestMethod: "GET",
                            onResponseReceieved: (data, code) => {
                                if (data && code === 200) {
                                    setBatches(data);
                                }
                            },
                        });
                        closeDialog();
                    } else {
                        showToast({ severity: "error", summary: "Failed", detail: assigned?.error || "Failed To Add To Batch !", life: 2000 });
                    }
                },
            });
        },
        [closeDialog, requestAPI, setBatches, showToast, userId],
    );

    return (
        <Dialog pt={{ content: { className: "overflow-y-auto" } }} header="Add To Batch" visible={visible} className="w-11" onHide={closeDialog}>
            <TabHeader className="pt-3" title="Add To Batch" highlights={["Batches this student is not part of"]} />

            <div className="mt-4 flex flex-column gap-2">
                {loading ? (
                    <Loading />
                ) : error ? (
                    <NoContent error={error} />
                ) : availableBatches?.length ? (
                    availableBatches.map((batch) => (
                        <div key={batch.id} className="flex gap-2 align-items-center border-1 border-gray-300 border-round py-2 px-3">
                            <div className="flex flex-column flex-1 gap-1 overflow-hidden">
                                <span className={`${TEXT_NORMAL} font-semibold`}>
                                    {batch.id}. {batch.title}
                                </span>
                                <span className={TEXT_SMALL}>
                                    {batch.branch_title || "No branch"} · {getReadableDate({ date: batch.start_date, removeTime: true })} –{" "}
                                    {getReadableDate({ date: batch.end_date, removeTime: true })}
                                </span>
                            </div>
                            <HasRequiredAuthority requiredAuthority={AUTHORITIES.ASSIGN_BATCH_STUDENT}>
                                <Button
                                    label="Add to this batch"
                                    icon="pi pi-plus"
                                    severity="warning"
                                    size="small"
                                    disabled={assigning}
                                    onClick={() => assignToBatch(batch.id)}
                                />
                            </HasRequiredAuthority>
                        </div>
                    ))
                ) : (
                    <NoContent error="No batches available" />
                )}
            </div>
        </Dialog>
    );
}
